import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AuthProvider } from "./authContext/AuthProvider";
import { AppProvider } from "./context/AppContext";
import { PrivateRoute } from "./authContext/PrivateRoute";

import Sidebar from "./components/Sidebar";

// Screens
import Dashboard from "./screens/Dashboard";
import LeadManagement from "./screens/LeadManagement";
import CallHistory from "./screens/CallHistory";
import LiveCall from "./screens/LiveCall";
import BatchCalling from "./screens/BatchCalling";
import QualifiedLeadsPage from "./screens/QualifiedLeadsPage";
import CallsScheduledPage from "./screens/CallsScheduledPage";
import ProfileScreen from "./screens/ProfileScreen";
import InboundCallsPage from "./screens/InboundCallsPage";
import InboundSMSPage from "./screens/InboundSMSPage";
import SMSDetailsPage from "./pages/SMSDetailsPage";
import ReviewStatusPage from "./screens/ReviewStatusPage";
import SalesCalls from "./screens/SalesCalls";
import SeasonalCalls from "./screens/SeasonalCalls";
import B2bCalls from "./screens/B2bCalls";
import SiteReports from "./screens/SiteReports";

// Website Chatbot
import WebsiteChatbotLeadsPage from "./screens/WebsiteChatbotLeadsPage";

// Auth Screens
import LoginScreen from "./screens/LoginScreen";
import SignupScreen from "./screens/SignupScreen";
import ForgotPasswordScreen from "./screens/ForgotPasswordScreen";
import ResetPasswordScreen from "./screens/ResetPasswordScreen";

// React Query
import { useQueryClient } from "@tanstack/react-query";

import "./styles/App.css";
import "./styles/LiveCall.css";

/* ----------------------------------
   PROTECTED LAYOUT
---------------------------------- */
const ProtectedLayout = ({ children }) => (
  <div className="app-container">
    <Sidebar />
    <main className="main-content">{children}</main>
  </div>
);

/* ----------------------------------
   APP ROOT
---------------------------------- */
function App() {
  const queryClient = useQueryClient();

  return (
    <AuthProvider queryClient={queryClient}>
      <AppProvider queryClient={queryClient}>
        <ToastContainer position="top-right" />

        <Routes>
          {/* ---------- PUBLIC ROUTES ---------- */}
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/signup" element={<SignupScreen />} />
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />

          {/* ---------- PROTECTED ROUTES ---------- */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <Dashboard />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <Dashboard />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/proposals"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <LeadManagement />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/history"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <CallHistory />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/sales-calls"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <SalesCalls />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/seasonal-calls"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <SeasonalCalls />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/b2b-calls"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <B2bCalls />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/website-chatbot-leads"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <WebsiteChatbotLeadsPage />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/proposal-status"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <QualifiedLeadsPage />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/scheduled-calls"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <CallsScheduledPage />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/batch-calling"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <BatchCalling />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/live-call/:leadId"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <LiveCall />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <ProfileScreen />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />


<Route
            path="/inbound-calls"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <InboundCallsPage />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/inbound-sms"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <InboundSMSPage />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/sms-details"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <SMSDetailsPage />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/review-status"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <ReviewStatusPage />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          <Route
            path="/site-reports"
            element={
              <PrivateRoute>
                <ProtectedLayout>
                  <SiteReports />
                </ProtectedLayout>
              </PrivateRoute>
            }
          />

          {/* ---------- FALLBACK ---------- */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
